import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ListChecks, RefreshCw, Trash2, ShoppingCart, CalendarClock, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

export const ShoppingLists = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shopping-lists");
      setLists(data);
    } catch (e) {
      toast.error("Could not load your lists");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reorder = async (list_id) => {
    setReordering(list_id);
    try {
      const { data } = await api.post(`/shopping-lists/${list_id}/reorder`);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
    } finally { setReordering(null); }
  };

  const toggleRecur = async (list, value) => {
    try {
      await api.patch(`/shopping-lists/${list.list_id}`, { recur_every_terms: value });
      toast.success(value ? "We'll email you every term" : "Recurring reminders off");
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const del = async (list_id) => {
    if (!window.confirm("Delete this list?")) return;
    try {
      await api.delete(`/shopping-lists/${list_id}`);
      toast.success("List deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold flex items-center gap-2">
            <ListChecks className="w-8 h-8 text-indigo-600" />My class lists
          </h1>
          <p className="text-slate-600 mt-1 max-w-xl">Save any cart as a class list, then reorder in one tap every term. Verified teachers get automatic 15% off on retail lines.</p>
        </div>
        <Link to="/shop"><Button variant="outline" className="rounded-full" data-testid="lists-shop-btn"><ShoppingCart className="w-4 h-4 mr-1" />Add supplies</Button></Link>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-16">Loading…</div>
      ) : lists.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-slate-200" data-testid="lists-empty">
          <Sparkles className="w-10 h-10 text-indigo-300 mx-auto" />
          <div className="font-display text-xl font-bold mt-4">No lists yet</div>
          <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Add supplies to your cart, then tap <em>Save as class list</em> — perfect for pupils, staff rooms and back-to-school runs.</div>
          <Link to="/shop"><Button className="mt-5 rounded-full bg-indigo-600 hover:bg-indigo-700">Browse supplies</Button></Link>
        </div>
      ) : (
        <div className="space-y-4">
          {lists.map((list) => (
            <div key={list.list_id} className="p-6 bg-white rounded-2xl border border-slate-200 hover:border-indigo-200 transition-all" data-testid={`list-card-${list.list_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display font-extrabold text-xl">{list.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">{list.list_id}</div>
                  <div className="text-xs text-slate-500 mt-1">{list.items.length} item{list.items.length !== 1 ? "s" : ""} · saved {fmtDate(list.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => reorder(list.list_id)}
                    disabled={reordering === list.list_id}
                    className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                    data-testid={`list-reorder-${list.list_id}`}
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${reordering === list.list_id ? "animate-spin" : ""}`} />
                    {reordering === list.list_id ? "Redirecting…" : "Reorder now"}
                  </Button>
                  <button
                    onClick={() => del(list.list_id)}
                    className="text-slate-400 hover:text-red-500 p-2"
                    data-testid={`list-delete-${list.list_id}`}
                    title="Delete list"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mt-5">
                {list.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 pr-3 bg-slate-50 rounded-full" data-testid={`list-item-${list.list_id}-${i}`}>
                    {it.image && <img src={it.image} alt="" className="w-10 h-10 rounded-full object-cover" />}
                    <div className="text-sm">
                      <div className="font-semibold line-clamp-1 max-w-[240px]">{it.name || it.product_id}</div>
                      <div className="text-xs text-slate-500">× {it.quantity}{it.bulk ? " · wholesale" : ""}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={!!list.recur_every_terms}
                    onCheckedChange={(v) => toggleRecur(list, v)}
                    data-testid={`list-recur-toggle-${list.list_id}`}
                  />
                  <div>
                    <div className="text-sm font-semibold">Every school term</div>
                    <div className="text-xs text-slate-500">Email me a one-tap reorder link at the start of Jan · Apr · Sept.</div>
                  </div>
                </div>
                {list.recur_every_terms && list.next_reorder_at && (
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full" data-testid={`list-next-${list.list_id}`}>
                    <CalendarClock className="w-3 h-3" />
                    Next reminder {fmtDate(list.next_reorder_at)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
